import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '@/lib/apiAuth';
import { supabaseAdmin } from '@/lib/supabaseAdmin';
import { sendEmail } from '@/lib/email';

// =============================================
// Send Invoice via Email
// =============================================

function buildDefaultEmailHtml(invoice: Record<string, unknown>): string {
  const numero = invoice.numero as string;
  const total = invoice.total_factura as number;
  const moneda = (invoice.moneda as string) || 'EUR';
  const fecha = invoice.fecha_emision as string;
  const clienteNombre = invoice.cliente_nombre as string;

  return `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <div style="background: #1a56db; color: white; padding: 16px 24px; border-radius: 8px 8px 0 0;">
        <h2 style="margin: 0;">Factura ${numero}</h2>
      </div>
      <div style="border: 1px solid #e5e7eb; border-top: none; padding: 24px; border-radius: 0 0 8px 8px;">
        <p style="font-size: 16px; color: #374151;">
          Estimado/a ${clienteNombre},
        </p>
        <p style="font-size: 16px; color: #374151;">
          Le adjuntamos la factura <strong>${numero}</strong> con fecha ${fecha}.
        </p>
        <div style="background: #f3f4f6; padding: 16px; border-radius: 8px; margin: 16px 0;">
          <p style="margin: 0; font-size: 14px; color: #6b7280;">Total factura</p>
          <p style="margin: 4px 0 0; font-size: 24px; font-weight: bold; color: #1a56db;">
            ${Number(total).toFixed(2)} ${moneda}
          </p>
        </div>
        <p style="color: #6b7280; font-size: 14px;">
          Si tiene alguna duda sobre esta factura, no dude en contactarnos.
        </p>
        <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 16px 0;" />
        <p style="color: #9ca3af; font-size: 12px;">
          Este email fue generado automáticamente por Externaliza2.
        </p>
      </div>
    </div>
  `;
}

export default withAuth(async (req, res, _user) => {
  if (req.method !== 'POST') {
    return res.status(405).json({ error: 'Método no permitido' });
  }

  try {
    const { invoice_id, email, template_id } = req.body;

    if (!invoice_id) {
      return res.status(400).json({ error: 'invoice_id es requerido' });
    }

    // Fetch invoice
    const { data: invoice, error: fetchError } = await supabaseAdmin
      .from('invoices')
      .select('*')
      .eq('id', invoice_id)
      .single();

    if (fetchError || !invoice) {
      return res.status(404).json({ error: 'Factura no encontrada' });
    }

    // Determine recipient email
    const recipientEmail = email || invoice.cliente_email;
    if (!recipientEmail) {
      return res.status(400).json({
        error: 'No se ha especificado email de destino y la factura no tiene email de cliente',
      });
    }

    // Build email content
    let subject = `Factura ${invoice.numero}`;
    let html = buildDefaultEmailHtml(invoice);

    // If template_id provided, use email template
    if (template_id) {
      const { data: template } = await supabaseAdmin
        .from('invoice_email_templates')
        .select('*')
        .eq('id', template_id)
        .single();

      if (template) {
        // Replace placeholders in template
        subject = (template.asunto as string)
          .replace(/\{numero\}/g, invoice.numero)
          .replace(/\{cliente_nombre\}/g, invoice.cliente_nombre)
          .replace(/\{total\}/g, Number(invoice.total_factura).toFixed(2))
          .replace(/\{moneda\}/g, invoice.moneda || 'EUR')
          .replace(/\{fecha\}/g, invoice.fecha_emision);

        html = (template.cuerpo_html as string)
          .replace(/\{numero\}/g, invoice.numero)
          .replace(/\{cliente_nombre\}/g, invoice.cliente_nombre)
          .replace(/\{total\}/g, Number(invoice.total_factura).toFixed(2))
          .replace(/\{moneda\}/g, invoice.moneda || 'EUR')
          .replace(/\{fecha\}/g, invoice.fecha_emision)
          .replace(/\{cliente_nif\}/g, invoice.cliente_nif);
      }
    } else {
      // Check if there's a default email template for this empresa
      const { data: defaultTemplate } = await supabaseAdmin
        .from('invoice_email_templates')
        .select('*')
        .eq('empresa_id', invoice.empresa_id)
        .eq('es_default', true)
        .limit(1);

      if (defaultTemplate && defaultTemplate.length > 0) {
        const template = defaultTemplate[0];
        subject = (template.asunto as string)
          .replace(/\{numero\}/g, invoice.numero)
          .replace(/\{cliente_nombre\}/g, invoice.cliente_nombre)
          .replace(/\{total\}/g, Number(invoice.total_factura).toFixed(2))
          .replace(/\{moneda\}/g, invoice.moneda || 'EUR')
          .replace(/\{fecha\}/g, invoice.fecha_emision);

        html = (template.cuerpo_html as string)
          .replace(/\{numero\}/g, invoice.numero)
          .replace(/\{cliente_nombre\}/g, invoice.cliente_nombre)
          .replace(/\{total\}/g, Number(invoice.total_factura).toFixed(2))
          .replace(/\{moneda\}/g, invoice.moneda || 'EUR')
          .replace(/\{fecha\}/g, invoice.fecha_emision)
          .replace(/\{cliente_nif\}/g, invoice.cliente_nif);
      }
    }

    // Send email
    const emailResult = await sendEmail({
      to: recipientEmail,
      subject,
      html,
    });

    if (!emailResult.success) {
      return res.status(500).json({
        error: `Error al enviar email: ${emailResult.error}`,
      });
    }

    // Update invoice email status
    const now = new Date().toISOString();
    const updateData: Record<string, unknown> = {
      email_enviado: true,
      email_fecha_envio: now,
      updated_at: now,
    };

    // If invoice was emitida, update to enviada
    if (invoice.estado === 'emitida') {
      updateData.estado = 'enviada';
    }

    await supabaseAdmin
      .from('invoices')
      .update(updateData)
      .eq('id', invoice_id);

    return res.status(200).json({
      success: true,
      message: `Email enviado a ${recipientEmail}`,
      emailId: emailResult.id,
    });
  } catch (error: any) {
    console.error('[API send-email] Unexpected error:', error);
    return res.status(500).json({ error: error.message || 'Error interno del servidor' });
  }
});
